import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import adminApi from '../../lib/adminApi';
import { toast } from 'sonner';
import { GestorePageHeader } from '../../components/gestore/GestorePageHeader';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Laptop, Smartphone, Ban, ShieldCheck, Unplug, MapPin, Building2, Save } from 'lucide-react';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch (e) { return iso; }
};

export default function GestoreDevices() {
  const { t } = useTranslation();
  const [devices, setDevices] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [filter, setFilter] = useState('all');
  const [confirm, setConfirm] = useState(null); // { title, description, action, destructive }
  const [limits, setLimits] = useState({ pc: '', mobile: '' });
  const [savingLimits, setSavingLimits] = useState(false);

  const loadDevices = async () => {
    const { data } = await adminApi.get('/admin/devices', { params: filter === 'all' ? {} : { restaurant_id: filter } });
    setDevices(data);
  };
  const loadRestaurants = async () => {
    const { data } = await adminApi.get('/admin/restaurants');
    setRestaurants(data);
  };
  useEffect(() => { loadRestaurants(); }, []);
  useEffect(() => { loadDevices(); }, [filter]); // eslint-disable-line

  const selectedRestaurant = useMemo(
    () => restaurants.find((r) => r.id === filter), [restaurants, filter]
  );

  useEffect(() => {
    if (selectedRestaurant) {
      const dl = selectedRestaurant.device_limits || {};
      setLimits({
        pc: dl.pc === null || dl.pc === undefined ? '' : String(dl.pc),
        mobile: dl.mobile === null || dl.mobile === undefined ? '' : String(dl.mobile),
      });
    }
  }, [selectedRestaurant]);

  const restName = (id) => restaurants.find((r) => r.id === id)?.name || '—';

  const ask = (cfg) => setConfirm(cfg);
  const runConfirm = async () => { const c = confirm; setConfirm(null); if (c?.action) await c.action(); };

  const doBlock = async (d) => {
    try { await adminApi.post(`/admin/devices/${d.id}/block`); toast.success(t('gestore.dBlocked')); loadDevices(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('gestore.dError')); }
  };
  const doUnblock = async (d) => {
    try { await adminApi.post(`/admin/devices/${d.id}/unblock`); toast.success(t('gestore.dUnblocked')); loadDevices(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('gestore.dError')); }
  };
  const doDisconnect = async (d) => {
    try { await adminApi.delete(`/admin/devices/${d.id}`); toast.success(t('gestore.dDisconnected')); loadDevices(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('gestore.dError')); }
  };

  const askDisconnect = (d) => ask({
    title: t('gestore.dDisconnectTitle'), description: t('gestore.dDisconnectDesc'),
    destructive: true, action: () => doDisconnect(d),
  });
  const askBlock = (d) => ask({
    title: t('gestore.dBlockTitle'), description: t('gestore.dBlockDesc'),
    destructive: true, action: () => doBlock(d),
  });

  const saveLimits = async () => {
    if (!selectedRestaurant) return;
    setSavingLimits(true);
    try {
      const payload = {
        pc: limits.pc.trim() === '' ? null : Math.max(0, parseInt(limits.pc, 10) || 0),
        mobile: limits.mobile.trim() === '' ? null : Math.max(0, parseInt(limits.mobile, 10) || 0),
      };
      await adminApi.put(`/admin/restaurants/${selectedRestaurant.id}/device-limits`, payload);
      toast.success(t('gestore.dLimitsSaved'));
      loadRestaurants();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('gestore.dError'));
    } finally { setSavingLimits(false); }
  };

  const TypeBadge = ({ d }) => (
    <Badge variant="secondary" className="gap-1">
      {d.type === 'mobile' ? <Smartphone className="h-3 w-3" /> : <Laptop className="h-3 w-3" />}
      {d.type === 'mobile' ? t('gestore.dTypeMobile') : t('gestore.dTypePc')}
    </Badge>
  );

  const StatusBadge = ({ d }) => (
    d.blocked
      ? <Badge variant="destructive">{t('gestore.dStatusBlocked')}</Badge>
      : <Badge variant="default">{t('gestore.dStatusActive')}</Badge>
  );

  const DeviceActions = ({ d, stacked }) => (
    <div className={stacked ? 'flex flex-col gap-2 sm:flex-row' : 'flex items-center justify-end gap-1'}>
      {d.blocked ? (
        <Button variant="secondary" size={stacked ? 'sm' : 'sm'} className={stacked ? 'w-full sm:w-auto' : ''}
          onClick={() => doUnblock(d)} data-testid={`gestore-device-unblock-${d.id}`}>
          <ShieldCheck className="h-4 w-4 mr-1" /> {t('gestore.dUnblock')}
        </Button>
      ) : (
        <Button variant="ghost" size="sm" className={`text-destructive ${stacked ? 'w-full sm:w-auto' : ''}`}
          onClick={() => askBlock(d)} data-testid={`gestore-device-block-${d.id}`}>
          <Ban className="h-4 w-4 mr-1" /> {t('gestore.dBlock')}
        </Button>
      )}
      <Button variant="ghost" size="sm" className={`text-destructive ${stacked ? 'w-full sm:w-auto' : ''}`}
        onClick={() => askDisconnect(d)} data-testid={`gestore-device-disconnect-${d.id}`}>
        <Unplug className="h-4 w-4 mr-1" /> {t('gestore.dDisconnect')}
      </Button>
    </div>
  );

  const GeoLink = ({ d }) => (
    d.geo && d.geo.lat != null ? (
      <a href={`https://www.google.com/maps?q=${d.geo.lat},${d.geo.lng}`} target="_blank" rel="noreferrer"
        className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`gestore-device-map-${d.id}`}>
        <MapPin className="h-3.5 w-3.5" /> {t('gestore.dViewOnMap')}
      </a>
    ) : <span className="text-muted-foreground inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {t('gestore.dNoLocation')}</span>
  );

  return (
    <div className="px-4 md:px-6 lg:px-8 py-4 md:py-6">
      <GestorePageHeader title={t('gestore.dTitle')} subtitle={t('gestore.dSubtitle')}>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full sm:w-56" data-testid="gestore-devices-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('gestore.dAllRestaurants')}</SelectItem>
            {restaurants.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </GestorePageHeader>

      {/* Limits editor (only when a restaurant is selected) */}
      {selectedRestaurant && (
        <Card className="p-5 mb-4" data-testid="gestore-device-limits-card">
          <div className="font-semibold flex items-center gap-2 mb-1"><Building2 className="h-4 w-4" /> {t('gestore.dLimits')} — {selectedRestaurant.name}</div>
          <div className="text-xs text-muted-foreground mb-4">{t('gestore.dLimitsHint')}</div>
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1">
              <Label className="flex items-center gap-1.5"><Laptop className="h-3.5 w-3.5" /> {t('gestore.dLimitPc')}</Label>
              <Input type="number" min="0" inputMode="numeric" value={limits.pc}
                onChange={(e) => setLimits((s) => ({ ...s, pc: e.target.value }))}
                placeholder={t('gestore.dUnlimited')} className="mt-1.5" data-testid="gestore-limit-pc-input" />
            </div>
            <div className="flex-1">
              <Label className="flex items-center gap-1.5"><Smartphone className="h-3.5 w-3.5" /> {t('gestore.dLimitMobile')}</Label>
              <Input type="number" min="0" inputMode="numeric" value={limits.mobile}
                onChange={(e) => setLimits((s) => ({ ...s, mobile: e.target.value }))}
                placeholder={t('gestore.dUnlimited')} className="mt-1.5" data-testid="gestore-limit-mobile-input" />
            </div>
            <Button onClick={saveLimits} disabled={savingLimits} className="rounded-xl w-full sm:w-auto" data-testid="gestore-save-limits-button">
              <Save className="h-4 w-4 mr-2" /> {t('gestore.dSaveLimits')}
            </Button>
          </div>
        </Card>
      )}

      {devices.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="gestore-devices-empty">{t('gestore.dEmpty')}</Card>
      ) : (
        <>
          {/* Desktop table */}
          <Card className="p-0 hidden md:block overflow-hidden">
            <Table data-testid="gestore-devices-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('gestore.dColType')}</TableHead>
                  <TableHead>{t('gestore.dColRestaurant')}</TableHead>
                  <TableHead>{t('gestore.dColStatus')}</TableHead>
                  <TableHead>{t('gestore.dLocation')}</TableHead>
                  <TableHead>{t('gestore.dColLastSeen')}</TableHead>
                  <TableHead className="text-right">{t('gestore.dColActions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {devices.map((d) => (
                  <TableRow key={d.id} data-testid={`gestore-devices-row-${d.id}`}>
                    <TableCell><TypeBadge d={d} /></TableCell>
                    <TableCell className="text-sm">{d.restaurant_name || restName(d.restaurant_id)}</TableCell>
                    <TableCell><StatusBadge d={d} /></TableCell>
                    <TableCell className="text-sm"><GeoLink d={d} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground tabular">{fmtDate(d.last_seen)}</TableCell>
                    <TableCell className="text-right"><DeviceActions d={d} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* Mobile cards */}
          <div className="md:hidden flex flex-col gap-3" data-testid="gestore-devices-card-list">
            {devices.map((d) => (
              <Card key={d.id} className="p-4" data-testid={`gestore-device-card-${d.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold leading-tight flex items-center gap-1.5 truncate">
                      <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> {d.restaurant_name || restName(d.restaurant_id)}
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-2"><TypeBadge d={d} /><StatusBadge d={d} /></div>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-1.5 text-xs">
                  <div className="flex items-center gap-1.5"><GeoLink d={d} /></div>
                  <div className="text-muted-foreground">{t('gestore.dColLastSeen')}: <span className="text-foreground tabular">{fmtDate(d.last_seen)}</span></div>
                </div>
                <div className="mt-3"><DeviceActions d={d} stacked /></div>
              </Card>
            ))}
          </div>
        </>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">{t('gestore.dCancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={runConfirm} data-testid="confirm-ok"
              className={confirm?.destructive ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}>
              {t('gestore.dConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
